/**
 * pw-freebie-claim.js — Ticket 1 (TOF relaunch, 2026-07-18): free lead-magnet bundle claim.
 * POSTs {email, bundleHandle, attribution, consent} to PageWright's /api/leadmagnet-signup (real
 * entitlement mint + Resend email). On success, fires a GA4 generate_lead event carrying
 * whatever UTM params are present on this pageview, via assets/pw-analytics-gate.js (#1722: withheld
 * on staging/preview themes) — gtag itself is globally injected by the
 * Shopify GA4 app, no new wiring needed.
 *
 * `attribution` (2026-08-07, Paul: "we need UTM tracking to work else advertising is not
 * measurable") feeds the backend's later-purchase attribution bridge
 * (packages/adapters/src/lead-attribution.ts). Field names match packages/contracts/src/
 * attribution.ts's AttributionSchema (source/medium/campaign/term/content/gclid/fbclid/ttclid/
 * epik/msclkid/landingPage) — bare names, not the utm_-prefixed URL param names. bundleHandle
 * stays the static per-page section setting, exactly as before — never derived from
 * UTM/attribution, guarded by T5 in test_leadmagnet_origin_single_source_080606.py.
 *
 * FIRST-TOUCH ATTRIBUTION FIX (2026-09-11, overnight sweep — real, ongoing, unrecoverable data
 * loss found tonight): this used to build `attribution` fresh at SUBMIT time by reading
 * `window.location.search` in that instant (see the old `buildAttribution()`). A visitor who
 * clicks a paid ad — landing directly on this page with utm_source/gclid/etc in the URL, which is
 * how these lead-magnet campaigns actually work (see test_leadmagnet_origin_single_source_080606.py's
 * history) — then browses elsewhere on the site before coming BACK to actually claim the freebie
 * loses the query string by the time they submit: the attribution sent to the backend silently
 * became {}, and that ad spend was unattributable forever (you cannot retroactively attribute an
 * unrecorded visit).
 *
 * FIX: capture UTM/click-ids into sessionStorage ONCE, on page load (`captureFirstTouchAttribution`
 * below) — first-touch, sticky for the browser session. A later page load of this SAME page (the
 * "browsed away and came back" case) finds the earlier capture already stored and leaves it
 * untouched, exactly mirroring the philosophy `packages/api/src/fulfillment.ts` already applies
 * server-side (first-touch-sticky `lead_attribution` backfill) and `packages/domain/src/
 * attribution.ts`'s `captureAttribution` already applies client-side elsewhere in the pagewright
 * app. The submit handler now reads that STORED value via `getSubmitAttribution`, falling back to
 * a fresh live-URL parse only if storage produced nothing (unavailable/cleared/never captured) —
 * better to send something than silently send {}.
 *
 * Also adds ttclid (TikTok), epik (Pinterest), and msclkid (Microsoft/Bing) alongside the
 * pre-existing gclid/fbclid — these three paid channels were being silently dropped end-to-end
 * (client capture through to the `lead_attribution` table; see pagewright PR for the migration).
 *
 * Exported via a `typeof module` guard so `tests/mobile/lib/freebie-attribution.test.mjs` can
 * `node --test` the REAL production logic directly (no re-implementation to drift out of sync) —
 * inert in the browser, where `module` is simply undefined. All DOM wiring below is itself
 * guarded on `typeof document`, so requiring this file under Node never touches the DOM.
 */
(function (factory) {
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory();
  } else {
    factory();
  }
})(function () {
  'use strict';

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var UTM_TO_ATTRIBUTION_FIELD = {
    utm_source: 'source',
    utm_medium: 'medium',
    utm_campaign: 'campaign',
    utm_term: 'term',
    utm_content: 'content',
  };
  // Paid-channel click ids, captured verbatim under their own URL param name — matches
  // AttributionSchema's bare-name convention for gclid/fbclid; the 3 new ones (2026-09-11) follow
  // the same pattern: Google, Meta, TikTok, Pinterest, Microsoft/Bing.
  var CLICK_ID_KEYS = ['gclid', 'fbclid', 'ttclid', 'epik', 'msclkid'];

  // sessionStorage key for the first-touch capture. Scoped to this widget (not the pagewright
  // app's own `pw_attribution` key from packages/web/src/emitter.ts) — this is a standalone,
  // no-build-step theme asset with no dependency on that separate app/bundle, and its schema
  // (flat UTM+click-ids, no referrer-derived source classification) is intentionally simpler.
  var ATTRIBUTION_STORAGE_KEY = 'pw_freebie_attribution';

  function utmProps() {
    var params = new URLSearchParams(window.location.search);
    var props = {};
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) props[key] = value;
    });
    return props;
  }

  /**
   * The bound AttributionSchema (packages/contracts/src/attribution.ts) enforces on the five UTM
   * fields. Kept in sync BY VALUE with that `.max(64)` — see `clampUtmValue` for why a mismatch
   * here is a funnel outage rather than a cosmetic drift.
   */
  var UTM_MAX_LEN = 64;

  /**
   * tasks.db #949 nit (3) — clamp a UTM value to the schema bound at CAPTURE time.
   *
   * WHY TRUNCATE RATHER THAN SEND RAW: a >64-char utm_campaign (entirely plausible on real ad
   * platforms) fails `AttributionSchema` server-side, and `/api/leadmagnet-signup` rejects the
   * WHOLE claim with a 400 — the visitor gets no file at all. Since PR #216 made attribution
   * sticky in sessionStorage, that oversized value then persists for the entire session: every
   * retry re-sends it and re-fails, and even returning on a clean URL does not help because the
   * stored value wins. That is a self-inflicted per-session lockout on the lead-capture funnel,
   * caused by a few surplus characters in a campaign NAME.
   *
   * Truncating costs a few characters of a campaign label in reporting; rejecting costs the
   * entire lead. Applied only to the five bounded UTM fields — click ids are deliberately NOT
   * clamped (see `buildAttributionFromParams`).
   */
  function clampUtmValue(value) {
    return value.length > UTM_MAX_LEN ? value.slice(0, UTM_MAX_LEN) : value;
  }

  /**
   * Pure: build the AttributionSchema shape from a URLSearchParams + a landing-page path. No I/O
   * — safe to call from a unit test with a fake URLSearchParams and no DOM at all.
   */
  function buildAttributionFromParams(params, landingPage) {
    var attribution = {};
    Object.keys(UTM_TO_ATTRIBUTION_FIELD).forEach(function (key) {
      var value = params.get(key);
      if (value) attribution[UTM_TO_ATTRIBUTION_FIELD[key]] = clampUtmValue(value);
    });
    // Click ids are NOT clamped: AttributionSchema puts no bound on them, and they are opaque
    // platform-issued tokens that routinely exceed 64 chars (a real 130-char fbclid is in the
    // live lead_attribution table). Truncating one would corrupt a working identifier — the
    // exact opposite of the UTM case above, where truncating preserves an otherwise-lost lead.
    CLICK_ID_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) attribution[key] = value;
    });
    attribution.landingPage = landingPage;
    return attribution;
  }

  /**
   * tasks.db #949 nit (1) — GA4 props built from the STORED first-touch attribution.
   *
   * PR #216 fixed the sticky-first-touch read for the `/api/leadmagnet-signup` body but left the
   * `generate_lead` GA4 event one call site away still using `utmProps()`, a LIVE
   * `window.location.search` read at submit time. So the very journey PR #216 existed to fix —
   * click a paid ad, browse the site, come back and claim — still reported `{}` to GA4. Same
   * bug, same root cause, and GA4 is the surface Paul actually reads when judging ad
   * performance, so this half arguably mattered more than the one that got fixed.
   *
   * Maps the bare AttributionSchema field names back to the `utm_`-prefixed names GA4 already
   * receives, so the event's wire shape is unchanged — only its data source is corrected.
   * Deliberately forwards ONLY the five UTM fields: click ids / landingPage / firstTouchTs were
   * never part of this event and adding them here would be a silent analytics schema change.
   */
  function ga4PropsFromAttribution(attribution, bundleHandle) {
    var props = {};
    if (attribution) {
      Object.keys(UTM_TO_ATTRIBUTION_FIELD).forEach(function (urlKey) {
        var value = attribution[UTM_TO_ATTRIBUTION_FIELD[urlKey]];
        if (value) props[urlKey] = value;
      });
    }
    props.bundle_handle = bundleHandle;
    return props;
  }

  /*
   * GA4 lead-event value/currency (tasks.db #780 / CS-0095, 2026-09-10).
   *
   * THE BUG: as of an April 2026 GA4 validation change, a `generate_lead` event must carry BOTH a
   * `value` and a `currency` parameter to qualify as an Ads-importable key event. Both were
   * previously optional. GA4 gives no signal that they are missing — it still shows the event
   * starred as a key event — but the Google Ads conversion-import wizard silently omits it from
   * the importable list. That silent omission is why #780 read as an Ads bug for months: there is
   * nothing to see in GA4's own UI. This asset is the surface BOTH Winter2026 ads land on, so it
   * is the call site that actually mattered.
   *
   * LEAD_VALUE IS A PLACEHOLDER — PENDING PAUL'S DECISION. Paul has never set a lead value for
   * CollageSoup. Import eligibility requires only that `value` be present and numeric; the
   * magnitude matters solely once value-based bidding (tROAS / maximize conversion value) is
   * switched on, at which point this nominal 1 must be replaced with a real figure (e.g. average
   * first-order value x lead->customer conversion rate). Do not read this 1 as a considered
   * number, and do not turn on value-based bidding against it.
   *
   * LEAD_CURRENCY is kept in lockstep with the same constant in
   * assets/pw-search-collection-analytics.js: Google Ads imports ONE conversion action spanning
   * all three lead surfaces, so two surfaces disagreeing on currency would feed that single
   * action a mixed-currency stream.
   */
  var LEAD_VALUE = 1;
  var LEAD_CURRENCY = 'USD';

  /**
   * tasks.db #780/#949 — the full generate_lead payload: sticky first-touch UTM attribution (via
   * ga4PropsFromAttribution, tasks.db #949 nit (1)) + the value/currency pair GA4 now requires
   * (tasks.db #780). A thin wrapper, not a duplicate of ga4PropsFromAttribution's own logic — that
   * function stays focused on the attribution->utm_* mapping alone (and independently unit-tested
   * that way), while this one owns the additional GA4-import-eligibility fields.
   */
  function leadEventProps(attribution, bundleHandle) {
    var props = ga4PropsFromAttribution(attribution, bundleHandle);
    props.value = LEAD_VALUE;
    props.currency = LEAD_CURRENCY;
    return props;
  }

  // getItem/setItem can each throw independently (private browsing, storage disabled by policy),
  // separately from the property access on `window.sessionStorage` itself (see
  // `safeSessionStorage` below, used only in the real-browser DOM wiring) — every storage touch
  // in this file goes through one of these two so a claim can never crash over storage.
  function safeGetItem(storage, key) {
    try {
      return storage ? storage.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function safeSetItem(storage, key, value) {
    try {
      if (storage) storage.setItem(key, value);
    } catch (e) {
      /* sessionStorage unavailable — capture just doesn't persist this session */
    }
  }

  /**
   * 3-model review of #217 (2026-09-12, all three independently): `clampUtmValue` only runs at
   * CAPTURE time (`buildAttributionFromParams`). A session that captured an oversized UTM value
   * BEFORE this fix deployed — tab left open across the theme publish — keeps the raw, unclamped
   * value in sessionStorage: `readStoredAttribution`/`getSubmitAttribution` returned it verbatim,
   * so the claim kept 400ing and the GA4 event now newly reported the oversized value too. Re-
   * clamping here closes that gap AND doubles as defense-in-depth against any future writer of
   * `ATTRIBUTION_STORAGE_KEY` that forgets to clamp. Idempotent — clamping an already-clamped
   * value is a no-op — so this is safe to run on every read, not just the first one post-deploy.
   */
  function clampStoredAttributionUtmFields(attribution) {
    Object.keys(UTM_TO_ATTRIBUTION_FIELD).forEach(function (urlKey) {
      var field = UTM_TO_ATTRIBUTION_FIELD[urlKey];
      if (typeof attribution[field] === 'string') {
        attribution[field] = clampUtmValue(attribution[field]);
      }
    });
    return attribution;
  }

  function readStoredAttribution(storage) {
    var raw = safeGetItem(storage, ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? clampStoredAttributionUtmFields(parsed) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredAttribution(storage, attribution) {
    safeSetItem(storage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  }

  /**
   * tasks.db #949 nit (2) — does this attribution carry any REAL attribution signal (a UTM field
   * or a click id), as opposed to a bare `{ landingPage }` captured from a pure direct/organic
   * visit? Used by `captureFirstTouchAttribution` below to decide whether a stored value counts
   * as "already captured" or is still eligible to be upgraded by a later touch.
   */
  function hasAttributionSignal(attribution) {
    if (!attribution) return false;
    var hasUtm = Object.keys(UTM_TO_ATTRIBUTION_FIELD).some(function (urlKey) {
      return !!attribution[UTM_TO_ATTRIBUTION_FIELD[urlKey]];
    });
    if (hasUtm) return true;
    return CLICK_ID_KEYS.some(function (key) {
      return !!attribution[key];
    });
  }

  /**
   * First-touch, sticky — with one deliberate exception (tasks.db #949 nit (2), Paul's explicit
   * product decision, 2026-09-17): a stored value that carries NO real attribution signal (no
   * source/medium/campaign/term/content/click-id — just a bare `{ landingPage }` from a pure
   * direct/organic visit) is treated as "not yet captured." A later page load that DOES carry
   * real signal is then allowed to write in and become the captured first touch — a later real ad
   * click should not be discarded just because an earlier, uncredited direct visit got there
   * first.
   *
   * Once a stored value DOES carry real signal, it is fully sticky again: true first-touch-with-
   * signal semantics are unchanged, and no later touch (ad or otherwise) may overwrite it. This is
   * NOT "latest touch wins" — it only upgrades a signal-less placeholder, exactly once, to the
   * first touch that actually carries attribution.
   *
   * (Superseded rationale, pre-2026-09-17: a direct visit with zero params used to be treated as a
   * permanently sticky first touch in its own right, and a later ad click could never retroactively
   * claim credit for it. Paul's decision overrides that for the no-signal case specifically.)
   */
  function captureFirstTouchAttribution(storage, location) {
    var existing = readStoredAttribution(storage);
    if (existing && hasAttributionSignal(existing)) return existing;
    var params = new URLSearchParams(location.search);
    var attribution = buildAttributionFromParams(params, location.pathname);
    writeStoredAttribution(storage, attribution);
    return attribution;
  }

  /**
   * What the /api/leadmagnet-signup POST actually sends. Reads the sticky first-touch value
   * captured on page load; falls back to a fresh live-URL parse ONLY when storage produced
   * nothing (unavailable, cleared, or the page-load capture somehow never ran) — better to send
   * something than silently send {}.
   */
  function getSubmitAttribution(storage, location) {
    var stored = readStoredAttribution(storage);
    if (stored) return stored;
    var params = new URLSearchParams(location.search);
    return buildAttributionFromParams(params, location.pathname);
  }

  var api = {
    UTM_KEYS: UTM_KEYS,
    CLICK_ID_KEYS: CLICK_ID_KEYS,
    ATTRIBUTION_STORAGE_KEY: ATTRIBUTION_STORAGE_KEY,
    utmProps: utmProps,
    buildAttributionFromParams: buildAttributionFromParams,
    ga4PropsFromAttribution: ga4PropsFromAttribution,
    leadEventProps: leadEventProps,
    readStoredAttribution: readStoredAttribution,
    writeStoredAttribution: writeStoredAttribution,
    hasAttributionSignal: hasAttributionSignal,
    captureFirstTouchAttribution: captureFirstTouchAttribution,
    getSubmitAttribution: getSubmitAttribution,
  };

  // Running under Node (unit tests) — export the pure API and skip every DOM touch below.
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return api;
  }

  function safeSessionStorage() {
    try {
      return window.sessionStorage;
    } catch (e) {
      // Reading the PROPERTY itself can throw (SecurityError) in some locked-down browser
      // configurations, separately from getItem/setItem throwing on an object that DID resolve.
      return null;
    }
  }

  function showStatus(el, message, isError) {
    el.hidden = false;
    el.textContent = message;
    if (isError) {
      el.setAttribute('data-error', '');
    } else {
      el.removeAttribute('data-error');
    }
  }

  var sessionAttributionStorage = safeSessionStorage();

  // Capture-on-load (2026-09-11 fix): first touch, once, as soon as this script runs — BEFORE any
  // form is wired or submitted. Deliberately unconditional on this page's own load, not deferred
  // until a claim attempt, since the whole point is to catch the URL's UTM/click-ids at the
  // moment the visitor actually lands here (which is when a paid ad sends them), not at whatever
  // later moment they get around to submitting.
  captureFirstTouchAttribution(sessionAttributionStorage, window.location);

  // ── Server-issued form token (signup bot defense P2, 2026-09-28) ────────────────────────────────
  // GET /api/signup-token?ep=leadmagnet when the form mounts; sent back as `formToken`. The SERVER
  // stamps the issue time (a client-declared timer is a claim any bot can lie about). A token
  // endpoint that is down, 404s (API not deployed yet) or answers `token: null` (guard off) NEVER
  // blocks a claim: the form posts without a token and the server decides.
  function fetchGuardToken(backendUrl, endpoint) {
    try {
      return fetch(backendUrl + '/api/signup-token?ep=' + endpoint, { method: 'GET', credentials: 'omit' })
        .then(function (res) {
          return res && res.ok ? res.json() : null;
        })
        .then(function (body) {
          return body && body.token ? body.token : null;
        })
        .catch(function () {
          return null;
        });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  // ── Cloudflare Turnstile loader (signup bot defense P3; kept in sync with pw-newsletter-signup.js)
  // Only when the theme setting `turnstile_site_key` is set (blank = off). Fails OPEN client-side:
  // a script/widget failure lets the claim post without a token and the SERVER decides.
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var turnstileWaiters = null;
  function ensureTurnstile(cb) {
    if (window.turnstile) return cb(true);
    if (turnstileWaiters) return turnstileWaiters.push(cb);
    turnstileWaiters = [cb];
    var done = function (ok) {
      var w = turnstileWaiters;
      turnstileWaiters = null;
      w.forEach(function (fn) {
        fn(ok);
      });
    };
    try {
      var el = document.createElement('script');
      el.src = TURNSTILE_SRC;
      el.async = true;
      el.defer = true;
      el.onload = function () {
        done(!!window.turnstile);
      };
      el.onerror = function () {
        done(false);
      };
      document.head.appendChild(el);
    } catch (e) {
      done(false);
    }
  }

  document.querySelectorAll('[data-pw-freebie-form]').forEach(function (form) {
    var section = form.closest('[data-backend-url]');
    var backendUrl = section ? section.getAttribute('data-backend-url') : '';
    var bundleHandle = section ? section.getAttribute('data-bundle-handle') : '';
    var statusEl = form.querySelector('[data-pw-freebie-status]');
    var submitBtn = form.querySelector('[data-pw-freebie-submit]');
    var emailInput = form.querySelector('input[type="email"]');
    var consentInput = form.querySelector('[data-pw-freebie-consent]');
    // Thank-you-page direct download (2026-09-03) — the "ask" UI (email input + consent +
    // discount terms) gets swapped for a direct download link once a claim actually succeeds.
    var askEl = form.querySelector('[data-pw-freebie-ask]');
    var thankyouEl = form.querySelector('[data-pw-freebie-thankyou]');
    var downloadLinkEl = form.querySelector('[data-pw-freebie-download]');
    // Honeypot (P2): off-canvas, unfocusable; a filled value tells the server this was a bot.
    var hpInput = form.querySelector('[data-pw-freebie-hp]');
    var tokenPromise = fetchGuardToken(backendUrl, 'leadmagnet');
    var siteKey = section ? section.getAttribute('data-turnstile-sitekey') || '' : '';
    var tsBox = form.querySelector('[data-pw-turnstile]');
    var tsWidget = null;
    var tsToken = '';
    var tsFailed = false;
    var tsPending = false;
    function resetTurnstile() {
      tsToken = '';
      if (siteKey && tsWidget !== null && window.turnstile && window.turnstile.reset) window.turnstile.reset(tsWidget);
    }
    function runPending() {
      tsPending = false;
      onSubmit({ preventDefault: function () {} });
    }
    if (siteKey && tsBox) {
      ensureTurnstile(function (ok) {
        if (!ok) {
          tsFailed = true;
          if (tsPending) runPending();
          return;
        }
        if (tsWidget !== null) return;
        tsWidget = window.turnstile.render(tsBox, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: function (t) {
            tsToken = t;
            if (tsPending) runPending();
          },
          'error-callback': function () {
            tsFailed = true;
            if (tsPending) runPending();
          },
          'expired-callback': function () {
            tsToken = '';
          },
        });
      });
    }

    // The button ships hardcoded `disabled` in the liquid purely as a pre-JS gate (a click
    // before this deferred script wires the handler would do a useless native GET submit,
    // leaking the email into the URL); enable it here once wiring is done. It is deliberately
    // NOT consent-gated any more (2026-08-30): a disabled button fires no click/submit events,
    // so a visitor who skipped the required checkbox got ZERO feedback. Consent gating now
    // lives in the submit handler below, which shows a visible message instead. The checkbox
    // itself stays required + unchecked by default (Paul, 2026-08-07 -- newsletter signups
    // must never be pre-ticked).
    submitBtn.removeAttribute('disabled');
    if (consentInput) {
      consentInput.addEventListener('change', function () {
        // Clear the consent nudge as soon as the box is ticked -- no stale error.
        if (consentInput.checked) statusEl.hidden = true;
      });
    }
    // Observability (2026-09-03): a click-through on the direct download link is the metric
    // that tells us this fix actually worked. Fired once, wired at init (not inside the submit
    // handler) since the link only ever appears after a successful claim.
    if (downloadLinkEl) {
      downloadLinkEl.addEventListener('click', function () {
        // #1722: through the gate -- withheld on staging/preview themes (assets/pw-analytics-gate.js).
        if (window.PWAnalyticsGate) window.PWAnalyticsGate.gtagEvent('freebie_direct_download_click', { bundle_handle: bundleHandle });
      });
    }

    function onSubmit(evt) {
      evt.preventDefault();
      // Empty email is visitor-actionable: show the same visible feedback pattern as the
      // consent guard below (2026-08-30, same silent-failure class the reviewers flagged).
      if (!emailInput.value) {
        showStatus(statusEl, 'Please enter your email address.', true);
        emailInput.focus();
        return;
      }
      // A missing bundleHandle is a theme-config/developer error (the section setting wasn't
      // filled in), not something a real visitor can fix -- stays a silent return rather than
      // showing a customer-facing error for our own mistake.
      if (!bundleHandle) return;
      // Consent gate + defense in depth: the POST must never fire with the box unchecked
      // (covers button clicks, Enter-key submits, and scripted form.submit()). The form has
      // `novalidate` (house pattern), so this message IS the visitor-facing feedback --
      // native constraint validation never fires here.
      if (consentInput && !consentInput.checked) {
        showStatus(statusEl, 'Please check the box above so we can email you your free download.', true);
        consentInput.focus();
        return;
      }

      // Turnstile (P3): wait for the token if the widget is still working (no lost claim).
      if (siteKey && tsBox && !tsToken && !tsFailed) {
        tsPending = true;
        showStatus(statusEl, 'One moment — verifying you’re human…', false);
        return;
      }

      submitBtn.disabled = true;
      statusEl.hidden = true;

      // 2026-09-11 fix: read the STICKY first-touch attribution captured on page load, not a
      // fresh parse of the (possibly by-now-empty) live URL. See getSubmitAttribution's doc
      // comment above.
      var attribution = getSubmitAttribution(sessionAttributionStorage, window.location);

      tokenPromise.then(function (token) {
      return fetch(backendUrl + '/api/leadmagnet-signup', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // consent: true (#878): the backend requires a literal true; the !consentInput.checked
        // guard above returned before this fetch, so consent is guaranteed checked here.
        // website + formToken (P2): honeypot + server-issued token (undefined = omitted).
        body: JSON.stringify({ email: emailInput.value, bundleHandle: bundleHandle, attribution: attribution, consent: true, website: hpInput ? hpInput.value : '', formToken: token || undefined, turnstileToken: tsToken || undefined }),
      })
        .then(function (res) {
          return res
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              return { ok: res.ok, status: res.status, body: body };
            });
        })
        .then(function (result) {
          resetTurnstile(); // Turnstile tokens are single-use
          // The form token is single-use too, and the server may have consumed it: always fetch a
          // fresh one after an attempt (review fix) so a retry after a 5xx is not a spent-token replay.
          tokenPromise = fetchGuardToken(backendUrl, 'leadmagnet');
          if (result.ok) {
            showStatus(statusEl, result.body.message || 'Check your email for the download link.', false);
            form.reset();

            // leadEventProps: sticky first-touch UTMs (#949) + value/currency (#780).
            // #1722: through the gate -- withheld on staging/preview themes (assets/pw-analytics-gate.js).
            if (window.PWAnalyticsGate) window.PWAnalyticsGate.gtagEvent('generate_lead', leadEventProps(attribution, bundleHandle));

            // Thank-you-page direct download (2026-09-03): the backend now always returns
            // downloadUrl on a successful claim -- the SAME signed link the backup email
            // carries. Swap the ask UI for a thank-you state with a direct, immediately
            // clickable download link, so a visitor never HAS to leave the site and check
            // email just to get the file. Gated on downloadUrl actually being present so an
            // older/rolled-back backend response can never leave the button pointing at "#".
            if (result.body.downloadUrl && downloadLinkEl && thankyouEl) {
              downloadLinkEl.href = result.body.downloadUrl;
              thankyouEl.hidden = false;
              if (askEl) askEl.hidden = true;
            }
          } else if (result.status === 429) {
            showStatus(statusEl, 'Too many requests — please wait a bit and try again.', true);
            submitBtn.disabled = false;
          } else if (result.body && /^(token_invalid|try_again|email_not_accepted|turnstile_fail)$/.test(result.body.code || '')) {
            // The guard's own, visitor-actionable refusals. Tokens are single-use and time-bound, so
            // fetch a fresh one for the next attempt.
            showStatus(statusEl, result.body.message || 'Please reload the page and try again.', true);
            submitBtn.disabled = false;
          } else {
            showStatus(statusEl, 'Something went wrong — please try again in a moment.', true);
            submitBtn.disabled = false;
          }
        })
        .catch(function () {
          resetTurnstile();
          tokenPromise = fetchGuardToken(backendUrl, 'leadmagnet');
          showStatus(statusEl, 'Something went wrong — please try again in a moment.', true);
          submitBtn.disabled = false;
        });
      });
    }
    form.addEventListener('submit', onSubmit);
  });

  return api;
});
